import '@feral/ui/tokens.css'
import { APP_WINDOW } from '@feral/shared/client'
import { TooltipHost } from '@feral/ui'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { watchInstallPrompt } from '../lib/install.js'
import { endBootSplash } from '../lib/loader.js'
import { queryClient } from '../lib/query.js'
import { useSession } from '../lib/session.js'
import { captureSource } from '../lib/source.js'
import { watchVeil } from '../lib/veil.js'
import { watchShortViewport } from '../lib/viewport.js'
import { pageTransitions } from './motion.js'
import { router } from './router.js'

// The console's last lines, failed requests and errors nothing caught, for a bug report (bugs.md):
// loaded straight away, but apart, so the first screen doesn't wait for it.
void import('../lib/diag.js').then((d) => d.recordDiagnostics(queryClient))

// Venue responses used to be kept offline by URL, whoever was signed in (client review #8): that
// cache is no longer written, and what an older version left behind goes.
if ('caches' in window) caches.delete('spots').catch(() => {})

// A `?src=` tag on the link they came in by, before the router reads the address (GR-60).
captureSource()

// Chrome's install offer: kept for the Install screen and Settings (FR-ACC-08).
watchInstallPrompt()

// The console opens and returns to this window by name (APP_WINDOW), not a new one each time.
window.name = APP_WINDOW

useSession.getState().load()
// Pages change with the View Transitions API where the browser has it (BR-11).
pageTransitions(router)
watchShortViewport()
// Private things blur while the app is in the background (TS-09).
watchVeil()

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <TooltipHost />
    </QueryClientProvider>
  </StrictMode>,
)
// The app has painted its first screen: the splash fades (the map shows its own until it's drawn).
requestAnimationFrame(() => requestAnimationFrame(endBootSplash))
